'use client';

import React from 'react';
import styled from 'styled-components';
import { Skeleton } from '@/components/skeletons';

/**
 * StatCard Component Props
 */
export interface StatCardProps {
  /** Title/label for the statistic */
  label: string;
  /** Main value to display */
  value: string | number;
  /** Optional subtitle or additional info */
  subtitle?: string;
  /** Optional icon element */
  icon?: React.ReactNode;
  /** Variant style */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  /** Loading state - shows skeleton */
  loading?: boolean;
  /** Optional CSS class name */
  className?: string;
  /** Optional click handler */
  onClick?: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div<{ $clickable: boolean; $variant: string }>`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  transition: all 0.2s ease;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  position: relative;
  overflow: hidden;

  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return `
          border-color: rgba(251, 111, 42, 0.3);
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(270deg, #D33324 0%, #FB6F2A 100%);
          }
        `;
      case 'success':
        return `
          border-color: rgba(16, 185, 129, 0.3);
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(270deg, #059669 0%, #10b981 100%);
          }
        `;
      case 'warning':
        return `
          border-color: rgba(245, 158, 11, 0.3);
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(270deg, #d97706 0%, #f59e0b 100%);
          }
        `;
      case 'danger':
        return `
          border-color: rgba(239, 68, 68, 0.3);
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(270deg, #dc2626 0%, #ef4444 100%);
          }
        `;
      default:
        return '';
    }
  }}

  &:hover {
    border-color: ${({ $clickable }) =>
      $clickable ? '#d1d5db' : '#e5e7eb'};
    box-shadow: ${({ $clickable }) =>
      $clickable ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'};
    transform: ${({ $clickable }) =>
      $clickable ? 'translateY(-2px)' : 'none'};
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #6E6E6E;
  font-family: 'Open Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0px;
  flex: 1;
  line-height: 1.3;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #9ca3af;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Value = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #101018;
  line-height: 1.3;
  font-family: 'Open Sans', sans-serif;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Subtitle = styled.div`
  font-size: 12px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  color: #9ca3af;
  line-height: 1.4;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * StatCard Component
 * 
 * A card component for displaying statistics and metrics.
 * Perfect for dashboards and data visualization.
 * 
 * @component
 * @example
 * ```tsx
 * <StatCard
 *   label="Total Records"
 *   value="1165"
 *   variant="primary"
 * />
 * 
 * // With icon and subtitle
 * <StatCard
 *   label="Error Logs"
 *   value={1165}
 *   subtitle="All time errors"
 *   icon={<ErrorIcon />}
 *   variant="danger"
 * />
 * 
 * // Clickable
 * <StatCard
 *   label="Active Users"
 *   value="245"
 *   onClick={() => navigate('/users')}
 * />
 * ```
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subtitle,
  icon,
  variant = 'default',
  loading = false,
  className,
  onClick,
}) => {
  if (loading) {
    return (
      <Container
        $clickable={false}
        $variant={variant}
        className={className}
      >
        <Header>
          <Label>{label}</Label>
          {icon && <IconWrapper>{icon}</IconWrapper>}
        </Header>
        <div>
          <Skeleton width="50px" height="26px" />
        </div>
        {subtitle && (
          <div style={{ marginTop: '8px' }}>
            <Skeleton width="55%" height="14px" />
          </div>
        )}
      </Container>
    );
  }

  return (
    <Container
      $clickable={!!onClick}
      $variant={variant}
      onClick={onClick}
      className={className}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <Header>
        <Label>{label}</Label>
        {icon && <IconWrapper>{icon}</IconWrapper>}
      </Header>
      <Value>{typeof value === 'number' ? value.toLocaleString() : value}</Value>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
    </Container>
  );
};

export default StatCard;

